import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils/cn'

export interface FilterRailProps {
  /** Collapse state is owned by the caller (mirrors the module page's other view-state). */
  collapsed: boolean
  onToggleCollapsed: () => void
  /** Count shown as a badge on the collapsed rail when > 0. Default 0 (no badge). */
  activeCount?: number
  /** Header label shown when expanded, and folded into the collapse/expand
   *  aria-labels. Default "Filters". */
  label?: string
  /** Tailwind width class applied to the expanded rail. Default matches
   *  the original Margins rail (`w-[220px]`). */
  expandedWidthClassName?: string
  /** Rail content, shown only when expanded. */
  children: React.ReactNode
}

/**
 * Shared shell for the collapsible left filter rail used across module
 * pages (Margins, Quotes, Open Orders, Inventory, Projects, …): a
 * chevron toggle between a ~32px icon-only collapsed rail and a wider
 * expanded rail with a "Filters" header. Each module rail owns its own
 * filter *content* (date presets, status chips, text inputs, …) — this
 * primitive only owns the collapse/expand chrome so it doesn't drift
 * between modules.
 *
 * Extracted from `MarginsFilterRail` (Sprint — margin-thresholds
 * unification) so a second consumer (Product↔Customer Pricing's
 * upcoming filter rail) can compose the same shell instead of
 * re-implementing it.
 */
export function FilterRail({
  collapsed,
  onToggleCollapsed,
  activeCount = 0,
  label = 'Filters',
  expandedWidthClassName = 'w-[220px]',
  children,
}: FilterRailProps) {
  if (collapsed) {
    return (
      <aside
        className="flex w-8 flex-col items-center border-r border-border-subtle bg-surface-sunk"
        aria-label={`${label} (collapsed)`}
      >
        <button
          type="button"
          aria-label={`Expand ${label.toLowerCase()}`}
          onClick={onToggleCollapsed}
          className="mt-2 inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
        >
          <ChevronRight size={14} />
        </button>
        {activeCount > 0 ? (
          <span
            className="text-role-badge-count mt-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-subtle px-1.5 text-accent-text"
            aria-label={`${activeCount} active filter${activeCount === 1 ? '' : 's'}`}
          >
            {activeCount}
          </span>
        ) : null}
      </aside>
    )
  }

  return (
    <aside
      className={cn(
        'flex flex-shrink-0 flex-col border-r border-border-subtle bg-surface-sunk',
        expandedWidthClassName,
      )}
      aria-label={label}
    >
      <header className="flex items-center justify-between gap-2 border-b border-border-subtle px-3 py-2">
        <span className="text-role-section-label">{label}</span>
        <button
          type="button"
          aria-label={`Collapse ${label.toLowerCase()}`}
          onClick={onToggleCollapsed}
          className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
        >
          <ChevronLeft size={14} />
        </button>
      </header>

      <div className="flex flex-col gap-3 p-3">{children}</div>
    </aside>
  )
}
